import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  Close,
  Delete,
  Edit,
  Group,
  OpenInNew,
  Person,
  PersonRemove,
  ContentCopy,
  CallSplit,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Science,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { EditProviderPhoneDialog } from '../components/EditProviderPhoneDialog';
import { LoadingState } from '../components/loading-state';
import { ProviderBundleAssignmentsSection } from '../components/provider-bundle-assignments-section';
import { ProviderEncounterTypesSection } from '../components/provider-encounter-types-section';
import { ProviderFeatureFlags } from '../components/provider-feature-flags';
import { ProviderStatsigGates } from '../components/provider-statsig-gates';
import { ProviderEntitlementsSection } from '../components/provider-entitlements-section';
import { VoiceEnrollmentSection } from '../components/voice-enrollment-section';
import {
  useGetProviderAppointmentsQuery,
  useGetProviderEncountersQuery,
  useGetProviderInfoQuery,
  useGetProviderNotesQuery,
  useGetProviderNoteProcessingSessionsQuery,
  useGetProviderTranscriptionsQuery,
  useGetProviderGoogleSyncStatusQuery,
  useInitiateProviderGoogleSyncMutation,
  useDeleteProviderGoogleCalendarConfigMutation,
  useGetRecentStreamingThreadsQuery,
} from '../store/api';
import type { StreamingThreadListItem } from '../store/api';
import { useDeleteAdminNoteTemplateMutation, useForkTemplateForUserMutation, useGetAdminProviderTemplatesQuery, useRemoveUserTemplateMutation, useUpdateProviderProfileMutation } from '../store/hooks';
import { formatDateTime, formatRelativeTime, getStatusBadgeColor } from '../utils/formatting';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`provider-tabpanel-${index}`}
      aria-labelledby={`provider-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ProviderDetailPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Fork dialog state
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [bypassForkRestriction, setBypassForkRestriction] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Phone edit dialog state
  const [editPhoneDialogOpen, setEditPhoneDialogOpen] = useState(false);
  const [deletePhoneDialogOpen, setDeletePhoneDialogOpen] = useState(false);

  // Phone delete mutation
  const [deletePhone, { isLoading: isDeletingPhone }] = useUpdateProviderProfileMutation();

  // Fork mutation
  const [forkTemplate, { isLoading: isForking }] = useForkTemplateForUserMutation();

  // Template removal/deletion mutations
  const [removeUserTemplate, { isLoading: isRemoving }] = useRemoveUserTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteAdminNoteTemplateMutation();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [templateToRemove, setTemplateToRemove] = useState<any>(null);
  const [removeAction, setRemoveAction] = useState<'remove' | 'delete'>('remove');

  // Call all hooks unconditionally (React rules of hooks)
  const {
    data: provider,
    isLoading: userLoading,
    error: userError,
  } = useGetProviderInfoQuery({ providerId: providerId || '' }, { skip: !providerId });

  const {
    data: notesData,
    isLoading: _notesLoading,
    error: _notesError,
  } = useGetProviderNotesQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  const {
    data: transcriptionsData,
    isLoading: _transcriptionsLoading,
    error: _transcriptionsError,
  } = useGetProviderTranscriptionsQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  const {
    data: encountersData,
    isLoading: _encountersLoading,
    error: _encountersError,
  } = useGetProviderEncountersQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  const {
    data: appointmentsData,
    isLoading: _appointmentsLoading,
    error: _appointmentsError,
  } = useGetProviderAppointmentsQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  const {
    data: noteProcessingSessionsData,
    isLoading: _noteProcessingSessionsLoading,
    error: _noteProcessingSessionsError,
  } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  // Streaming threads data
  const {
    data: streamingThreadsData,
    isLoading: _streamingThreadsLoading,
    error: _streamingThreadsError,
  } = useGetRecentStreamingThreadsQuery(
    {
      userId: providerId || '',
      limit: 5,
      workflowName: 'note_chat',
    },
    { skip: !providerId },
  );

  // Google Calendar admin data
  const { data: googleSync, isLoading: googleSyncLoading } = useGetProviderGoogleSyncStatusQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );
  const [initiateSync, { isLoading: initiating }] = useInitiateProviderGoogleSyncMutation();
  const [deleteCalendar, { isLoading: deleting }] = useDeleteProviderGoogleCalendarConfigMutation();

  // Templates data - fetch templates that provider actually sees
  const {
    data: templatesData,
    isLoading: _templatesLoading,
    error: _templatesError,
  } = useGetAdminProviderTemplatesQuery(
    {
      providerId: providerId || '',
      organizationId: provider?.organization_id || '',
    },
    { skip: !providerId || !provider?.organization_id },
  );

  // Use response - templates are pre-sorted by display_order
  const templatesPreview = templatesData?.templates?.slice(0, 5) || [];
  const totalTemplates = templatesData?.templates?.length || 0;

  // All useCallback hooks must be called before any early returns (React Rules of Hooks)
  // Get URL for each row - Notes
  const getNotesRowUrl = useCallback((note: any) => {
    return `/admin-v2/note/${note.note_id}`;
  }, []);

  // Get state for each row - Notes
  const getNotesRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for each row - Transcriptions
  const getTranscriptionsRowUrl = useCallback((transcription: any) => {
    return `/admin-v2/transcript/${transcription.transcription_session_id}`;
  }, []);

  // Get state for each row - Transcriptions
  const getTranscriptionsRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for each row - Encounters
  const getEncountersRowUrl = useCallback((encounter: any) => {
    return `/admin-v2/encounter/${encounter.encounter_id}`;
  }, []);

  // Get state for each row - Encounters
  const getEncountersRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for each row - Appointments
  const getAppointmentsRowUrl = useCallback((appointment: any) => {
    return `/admin-v2/appointment/${appointment.appointment_id}`;
  }, []);

  // Get state for each row - Appointments
  const getAppointmentsRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for Notes view all
  const getNotesViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/notes`;
  }, [providerId]);

  // Get URL for Transcriptions view all
  const getTranscriptionsViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/transcriptions`;
  }, [providerId]);

  // Get URL for Encounters view all
  const getEncountersViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/encounters`;
  }, [providerId]);

  // Get URL for Appointments view all
  const getAppointmentsViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/appointments`;
  }, [providerId]);

  // Get URL for each row - Note Processing Sessions
  const getNoteProcessingSessionsRowUrl = useCallback((nps: any) => {
    return `/admin-v2/note-processing-session/${nps.note_processing_session_id}`;
  }, []);

  // Get state for each row - Note Processing Sessions
  const getNoteProcessingSessionsRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for Note Processing Sessions view all
  const getNoteProcessingSessionsViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/note-processing-sessions`;
  }, [providerId]);

  // Get URL for Templates view all
  const getTemplatesViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/templates`;
  }, [providerId]);

  const getStreamingThreadsRowUrl = useCallback((thread: StreamingThreadListItem) => {
    return `/admin-v2/streaming-thread/${thread.thread_id}`;
  }, []);

  // Fork template handler
  const handleOpenForkDialog = useCallback((template: any, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedTemplate(template);
    setBypassForkRestriction(false);
    setForkDialogOpen(true);
  }, []);

  const handleCloseForkDialog = useCallback(() => {
    setForkDialogOpen(false);
    setSelectedTemplate(null);
    setBypassForkRestriction(false);
  }, []);

  const handleForkTemplate = useCallback(async () => {
    if (!selectedTemplate || !providerId || !provider) return;

    try {
      const result = await forkTemplate({
        adminForkTemplateForUserRequest: {
          source_template_id: selectedTemplate.template?.id || '',
          target_user_id: providerId,
          target_organization_id: provider.organization_id || '',
          bypass_fork_restriction: bypassForkRestriction || undefined,
        },
      }).unwrap();

      handleCloseForkDialog();

      if (result.already_existed) {
        enqueueSnackbar('User already has a customized version of this template', {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('Template forked successfully', { variant: 'success' });
      }

      // Navigate to the forked template editor
      if (result.template?.id) {
        navigate(`/admin-v2/templates/${result.template.id}`);
      }
    } catch (error: any) {
      if (error?.status === 403) {
        enqueueSnackbar(
          "This template doesn't allow user forks. Enable 'Force fork' to override.",
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(
          error?.data?.message || 'Failed to fork template',
          { variant: 'error' },
        );
      }
    }
  }, [selectedTemplate, providerId, provider, bypassForkRestriction, forkTemplate, navigate, enqueueSnackbar, handleCloseForkDialog]);

  // Remove/delete template handlers
  const handleOpenRemoveDialog = useCallback((template: any, action: 'remove' | 'delete', e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setTemplateToRemove(template);
    setRemoveAction(action);
    setRemoveDialogOpen(true);
  }, []);

  const handleCloseRemoveDialog = useCallback(() => {
    setRemoveDialogOpen(false);
    setTemplateToRemove(null);
  }, []);

  const handleConfirmTemplateAction = useCallback(async () => {
    if (!templateToRemove || !providerId) return;
    const templateId = templateToRemove.template?.id || '';
    try {
      if (removeAction === 'delete') {
        await deleteTemplate({ templateId }).unwrap();
        enqueueSnackbar('Template permanently deleted', { variant: 'success' });
      } else {
        await removeUserTemplate({ userId: providerId, templateId }).unwrap();
        enqueueSnackbar('Template removed from user', { variant: 'success' });
      }
      handleCloseRemoveDialog();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || error?.data?.detail || 'Failed to remove template',
        { variant: 'error' },
      );
    }
  }, [templateToRemove, providerId, removeAction, deleteTemplate, removeUserTemplate, enqueueSnackbar, handleCloseRemoveDialog]);

  // State getter for "View All" actions
  const getViewAllState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Tab change handler
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  // If we don't have a providerId, we can't proceed
  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">
              No provider ID specified. Please search for the provider to view their details.
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Loading state
  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  // Error state
  if (userError || !provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading provider</h2>
            <p className="text-gray-600 mt-2">
              {userError && 'data' in userError
                ? JSON.stringify(userError.data)
                : 'Failed to load provider details. Please try again.'}
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const notesColumns = [
    {
      key: 'note_id',
      label: 'Note ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'template',
      label: 'Template',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'patient_name',
      label: 'Patient',
      render: (value: string | null) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'encounter_type',
      label: 'Type',
      render: (value: string | null) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'has_feedback',
      label: 'Feedback',
      render: (value: boolean) =>
        value ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Close className="h-4 w-4 text-red-500" />
        ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const transcriptionsColumns = [
    {
      key: 'transcription_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'start_time',
      label: 'Started',
      render: (value: string) => (
        <span className="text-sm text-gray-900">{value ? formatDateTime(value) : 'N/A'}</span>
      ),
    },
    {
      key: 'chunk_count',
      label: 'Chunks',
      render: (value: number) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'notes_created',
      label: 'Notes',
      render: (value: number) => (
        <span className={`text-sm font-medium ${value === 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value}
        </span>
      ),
    },
    {
      key: 'job_status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'is_closed',
      label: 'Closed',
      render: (value: boolean) =>
        value ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Close className="h-4 w-4 text-red-500" />
        ),
    },
  ];

  const encountersColumns = [
    {
      key: 'encounter_id',
      label: 'Encounter ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'encounter_type',
      label: 'Type',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'input_documents',
      label: 'Input Docs',
      render: (value: number) => (
        <span className={`text-sm font-medium ${value === 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const appointmentsColumns = [
    {
      key: 'appointment_id',
      label: 'Appointment ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'start_time',
      label: 'Scheduled',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value: number) => <span className="text-sm text-gray-500">{value} min</span>,
    },
  ];

  const noteProcessingSessionsColumns = [
    {
      key: 'note_processing_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'encounter_id',
      label: 'Encounter',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900 break-all font-mono">{value || 'N/A'}</span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'transcription_session_ids',
      label: 'Transcripts',
      render: (value: string[] | null) => (
        <span className="text-sm text-gray-900">{value?.length || 0}</span>
      ),
    },
    {
      key: 'note_id',
      label: 'Note',
      render: (value: string | null) =>
        value && value.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-green-600 break-all font-mono">{value}</span>
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  if (value) void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <span className="text-sm text-gray-400">No note</span>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const templatesColumns = [
    {
      key: 'title',
      label: 'Template Name',
      render: (_: unknown, row: any) => {
        const displayName = row.revision?.title || 'Unnamed Template';
        return (
          <span className="text-sm font-medium text-gray-900">{displayName}</span>
        );
      },
    },
    {
      key: 'template_id',
      label: 'Template ID',
      render: (_: unknown, row: any) => {
        const templateId = row.template?.id || '';
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-mono">
              {templateId ? templateId.substring(0, 20) + '...' : 'N/A'}
            </span>
            {templateId && (
              <Tooltip title="Copy ID">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    void navigator.clipboard.writeText(templateId);
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      key: 'owner_scope',
      label: 'Type',
      render: (_: unknown, row: any) => {
        const scope = row.template?.owner_scope || 'user';
        return (
          <Badge color={scope === 'public' ? 'primary' : scope === 'user' ? 'secondary' : 'default'}>
            {scope}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: any) => {
        const scope = row.template?.owner_scope || 'user';
        const isPublicOrOrg = scope === 'public' || scope === 'org';

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPublicOrOrg && (
              <Tooltip title="Fork for User">
                <IconButton
                  size="small"
                  onClick={e => handleOpenForkDialog(row, e)}
                  color="primary"
                >
                  <CallSplit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {scope === 'org' ? (
              <>
                <Tooltip title="Manage org templates">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      if (provider?.organization_id) {
                        navigate(`/admin-v2/organization/${provider.organization_id}`);
                      }
                    }}
                    color="primary"
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Org templates can only be removed by the organization manager">
                  <span>
                    <IconButton size="small" disabled>
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : scope === 'public' ? (
              <Tooltip title="Remove from this user's list">
                <IconButton
                  size="small"
                  onClick={e => handleOpenRemoveDialog(row, 'remove', e)}
                  color="warning"
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Permanently delete this template">
                <IconButton
                  size="small"
                  onClick={e => handleOpenRemoveDialog(row, 'delete', e)}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const streamingThreadsColumns = [
    {
      key: 'thread_id',
      label: 'Thread ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 font-mono">
            {value?.substring(0, 12)}...
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'initial_message',
      label: 'Initial Message',
      render: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      key: 'event_count',
      label: 'Events',
      render: (value: number) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'first_message_at',
      label: 'Started',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatRelativeTime(value)}</span>
      ),
    },
    {
      key: 'last_message_at',
      label: 'Last Activity',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatRelativeTime(value)}</span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>

        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  {provider.name}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Provider Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Person className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
                  
                  {/* Contact Information */}
                  <div className="flex flex-col gap-1 mt-2">
                    {provider.user_info?.email && (
                      <div className="flex items-center gap-1">
                        <EmailIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{provider.user_info.email}</span>
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={() => {
                              void navigator.clipboard.writeText(provider.user_info?.email || '');
                              enqueueSnackbar('Email copied to clipboard', { variant: 'success' });
                            }}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      {provider.user_info?.mobile_phone_num ? (
                        <>
                          <span className="text-sm text-gray-600">{provider.user_info.mobile_phone_num}</span>
                          <Tooltip title="Copy phone number">
                            <IconButton
                              size="small"
                              onClick={() => {
                                void navigator.clipboard.writeText(provider.user_info?.mobile_phone_num || '');
                                enqueueSnackbar('Phone number copied to clipboard', { variant: 'success' });
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No phone number</span>
                      )}
                      <Tooltip title={provider.user_info?.mobile_phone_num ? 'Edit phone number' : 'Add phone number'}>
                        <IconButton
                          size="small"
                          onClick={() => setEditPhoneDialogOpen(true)}
                        >
                          <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      {provider.user_info?.mobile_phone_num && (
                        <Tooltip title="Remove phone number">
                          <IconButton
                            size="small"
                            onClick={() => setDeletePhoneDialogOpen(true)}
                          >
                            <Delete sx={{ fontSize: 14, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <p className="text-sm text-gray-500">User ID: {provider.provider_id}</p>
                    <Tooltip title="Copy User ID">
                      <IconButton
                        size="small"
                        onClick={() => {
                          void navigator.clipboard.writeText(provider.provider_id);
                          enqueueSnackbar('User ID copied to clipboard', { variant: 'success' });
                        }}
                      >
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  {provider.organization_id && (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-500">Org ID: {provider.organization_id}</p>
                      <Tooltip title="Copy Organization ID">
                        <IconButton
                          size="small"
                          onClick={() => {
                            void navigator.clipboard.writeText(provider.organization_id || '');
                            enqueueSnackbar('Organization ID copied to clipboard', { variant: 'success' });
                          }}
                        >
                          <ContentCopy fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                  {provider.organization_name && (
                    <Button
                      variant="outlined"
                      size="small"
                      className="mt-2"
                      startIcon={<Group className="h-4 w-4" />}
                      onClick={() => {
                        if (provider.organization_id) {
                          navigate(`/admin-v2/organization/${provider.organization_id}`);
                        }
                      }}
                    >
                      {provider.organization_name} ({provider.role})
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-8">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Total Notes</div>
                  <div className="text-2xl font-bold text-gray-900">{notesData?.total || 0}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Paper sx={{ width: '100%', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Activity" />
            <Tab label="Access" />
            <Tab label="Content" />
            <Tab label="Settings" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* Quick Actions */}
          <div className="mb-6 flex gap-2">
            {provider.organization_id && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Science />}
                onClick={() => {
                  const params = new URLSearchParams({ organizationId: provider.organization_id || '' });
                  navigate(`/admin-v2/shadow-notes?${params.toString()}`);
                }}
              >
                Shadow Notes
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  `/admin-v2/provider/${encodeURIComponent(providerId || '')}/containers/patient_page`,
                )
              }
            >
              Patient Containers
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNew className="h-4 w-4" />}
              onClick={() => {
                const now = Date.now();
                const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
                const filters = {
                  filterStartTime: sevenDaysAgo,
                  filterEndTime: now,
                  filterEventsLogged: [
                    {
                      name: 'statsig::session_recording',
                      filters: [
                        {
                          property: { key: 'userID', column: 'user_object' },
                          operator: 'ANY_OF',
                          values: [provider.provider_id],
                        },
                      ],
                    },
                  ],
                };
                const url = `https://console.statsig.com/4x1Dwzy0iHPy51koMolh2U/session_replay?filters=${encodeURIComponent(JSON.stringify(filters))}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              View Session Replays in Statsig
            </Button>
          </div>

          {/* Data Tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 w-full">
            <DataTable
              title="Notes"
              columns={notesColumns as any}
              data={notesData?.items || []}
              total={notesData?.total}
              loading={_notesLoading}
              getRowUrl={getNotesRowUrl}
              onViewAll={() => {
                navigate(getNotesViewAllUrl());
              }}
              emptyMessage="No notes to display"
            />

            <DataTable
              title="Transcriptions"
              columns={transcriptionsColumns as any}
              data={transcriptionsData?.items || []}
              total={transcriptionsData?.total}
              loading={_transcriptionsLoading}
              getRowUrl={getTranscriptionsRowUrl}
              onViewAll={() => {
                navigate(getTranscriptionsViewAllUrl());
              }}
              emptyMessage="No transcriptions to display"
            />

            <DataTable
              title="Encounters"
              columns={encountersColumns as any}
              data={encountersData?.items || []}
              total={encountersData?.total}
              loading={_encountersLoading}
              getRowUrl={getEncountersRowUrl}
              onViewAll={() => {
                navigate(getEncountersViewAllUrl());
              }}
              emptyMessage="No encounters to display"
            />

            <DataTable
              title="Appointments"
              columns={appointmentsColumns as any}
              data={appointmentsData?.items || []}
              total={appointmentsData?.total}
              loading={_appointmentsLoading}
              getRowUrl={getAppointmentsRowUrl}
              onViewAll={() => {
                navigate(getAppointmentsViewAllUrl());
              }}
              emptyMessage="No appointments to display"
            />

            <DataTable
              title="Note Processing Sessions"
              columns={noteProcessingSessionsColumns as any}
              data={noteProcessingSessionsData?.items || []}
              total={noteProcessingSessionsData?.total}
              loading={_noteProcessingSessionsLoading}
              getRowUrl={getNoteProcessingSessionsRowUrl}
              onViewAll={() => {
                navigate(getNoteProcessingSessionsViewAllUrl());
              }}
              emptyMessage="No note processing sessions to display"
            />

            <DataTable<StreamingThreadListItem>
              title="Streaming Threads (Note Chat)"
              columns={streamingThreadsColumns as any}
              data={streamingThreadsData?.threads || []}
              total={streamingThreadsData?.threads?.length}
              loading={_streamingThreadsLoading}
              getRowUrl={getStreamingThreadsRowUrl}
              emptyMessage="No streaming threads found"
            />
          </div>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Bundle Assignments */}
          {provider.organization_id && (
            <div className="bg-white rounded-lg shadow mb-6">
              <details className="group">
                <summary className="cursor-pointer select-none px-6 py-4 flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Bundle Assignments</span>
                  <ChevronRight className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-5">
                  <ProviderBundleAssignmentsSection
                    userId={provider.provider_id}
                    organizationId={provider.organization_id}
                  />
                </div>
              </details>
            </div>
          )}

          {/* Entitlements */}
          {provider.organization_id && (
            <div className="bg-white rounded-lg shadow mb-6">
              <details className="group">
                <summary className="cursor-pointer select-none px-6 py-4 flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Entitlements</span>
                  <ChevronRight className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-5">
                  <ProviderEntitlementsSection
                    userId={provider.provider_id}
                    organizationId={provider.organization_id}
                  />
                </div>
              </details>
            </div>
          )}

          {/* Feature Flags */}
          <div className="bg-white rounded-lg shadow mb-6">
            <details className="group">
              <summary className="cursor-pointer select-none px-6 py-4 flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Feature Flags</span>
                <ChevronRight className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-5">
                <ProviderFeatureFlags
                  providerId={providerId || ''}
                  currentFeatureFlags={provider?.feature_flags}
                />
              </div>
            </details>
          </div>

          {/* Statsig Feature Gates */}
          <div className="bg-white rounded-lg shadow mb-6">
            <details className="group">
              <summary className="cursor-pointer select-none px-6 py-4 flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Statsig Feature Gates</span>
                <ChevronRight className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-5">
                <ProviderStatsigGates providerId={providerId || ''} />
              </div>
            </details>
          </div>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Encounter Types */}
          <div className="bg-white rounded-lg shadow mb-6">
            <details className="group" open>
              <summary className="cursor-pointer select-none px-6 py-4 flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Encounter Types</span>
                <ChevronRight className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-5">
                <ProviderEncounterTypesSection
                  providerId={providerId}
                  templates={templatesData?.templates}
                />
              </div>
            </details>
          </div>

          {/* Templates */}
          <DataTable
            title="Templates"
            columns={templatesColumns as any}
            data={templatesPreview}
            total={totalTemplates}
            loading={_templatesLoading}
            description={
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Same templates and order as the provider sees.
                </Typography>
              </Alert>
            }
            onRowClick={(row: any) => {
              // Navigate to template detail page
              const templateId = row.template?.id || '';
              const templateVersion = row.revision?.version || 1;
              const templateType = row.template?.owner_scope || 'user';
              navigate(
                `/admin-v2/provider/${encodeURIComponent(providerId)}/templates/${templateId}/${templateVersion}?type=${templateType}`,
              );
            }}
            onViewAll={totalTemplates > 5 ? () => {
              navigate(getTemplatesViewAllUrl());
            } : undefined}
            emptyMessage="No templates to display"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* Voice Enrollment Card */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Voice Enrollment</h2>
              </div>
              <VoiceEnrollmentSection userId={provider.provider_id} />
            </div>
          </div>

          {/* Google Calendar Card */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Google Calendar</h2>
              </div>
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-1">Sync Status</div>
                {googleSyncLoading ? (
                  <div className="text-sm text-gray-500">Loading…</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between rounded border px-3 py-2">
                      <span className="text-sm text-gray-700">Synced</span>
                      {googleSync?.is_configured ? (
                        <Badge color="success">true</Badge>
                      ) : (
                        <Badge color="error">false</Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between rounded border px-3 py-2">
                      <span className="text-sm text-gray-700">Integrated</span>
                      {googleSync?.is_integrated ? (
                        <Badge color="success">true</Badge>
                      ) : (
                        <Badge color="error">false</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {googleSync?.is_integrated ? (
                  <Button
                    variant="contained"
                    disabled={initiating}
                    onClick={() => {
                      if (!providerId) return;
                      void initiateSync({ providerId });
                    }}
                  >
                    create and sync calendar
                  </Button>
                ) : (
                  <Tooltip title="User must integrate Google Calendar in the app to enable syncing. This happens when they click the Sync Calendar button in the Schedule tab.">
                    <span>
                      <Button variant="contained" disabled>
                        initiate calendar sync
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Button
                  variant="outlined"
                  color="error"
                  disabled={deleting}
                  onClick={() => {
                    if (!providerId) return;
                    void deleteCalendar({ providerId });
                  }}
                >
                  delete calendar
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  disabled={deleting}
                  onClick={() => {
                    if (!providerId) return;
                    void deleteCalendar({ providerId, resetIntegrationFlag: true });
                  }}
                >
                  de-integrate from google
                </Button>
              </div>
            </div>
          </div>
        </TabPanel>
      </main>

      {/* Fork Template Dialog */}
      <Dialog
        open={forkDialogOpen}
        onClose={handleCloseForkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fork Template for User</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Create a personalized copy of this template for{' '}
                <strong>{provider?.name || providerId}</strong>.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Template:</strong>{' '}
                  {selectedTemplate.revision?.title || 'Unnamed Template'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Type:</strong> {selectedTemplate.template?.owner_scope || 'user'}
                </Typography>
              </Alert>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={bypassForkRestriction}
                    onChange={e => setBypassForkRestriction(e.target.checked)}
                  />
                }
                label="Force fork even if template doesn't allow forks"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                Enable this if the template has fork restrictions but you need to create a copy anyway.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForkDialog} disabled={isForking}>
            Cancel
          </Button>
          <Button
            onClick={handleForkTemplate}
            variant="contained"
            disabled={isForking || !selectedTemplate}
            startIcon={isForking ? <CircularProgress size={16} /> : <CallSplit />}
          >
            {isForking ? 'Forking...' : 'Fork Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove/Delete Template Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCloseRemoveDialog}
        maxWidth="xs"
      >
        <DialogTitle>
          {removeAction === 'delete' ? 'Permanently Delete Template' : 'Remove Template from User'}
        </DialogTitle>
        <DialogContent>
          {removeAction === 'delete' ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                This action cannot be undone.
              </Alert>
              <Typography>
                This will permanently delete{' '}
                <strong>{templateToRemove?.revision?.title || 'this template'}</strong>.
                It will no longer be available to{' '}
                <strong>{provider?.name || providerId}</strong> or anyone else.
              </Typography>
            </>
          ) : (
            <Typography>
              This will remove{' '}
              <strong>{templateToRemove?.revision?.title || 'this template'}</strong>{' '}
              from <strong>{provider?.name || providerId}</strong>&apos;s template list.
              The template will still be available to other users.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog} disabled={isRemoving || isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTemplateAction}
            variant="contained"
            color="error"
            disabled={isRemoving || isDeleting}
            startIcon={(isRemoving || isDeleting) ? <CircularProgress size={16} /> : undefined}
          >
            {(isRemoving || isDeleting)
              ? (removeAction === 'delete' ? 'Deleting...' : 'Removing...')
              : (removeAction === 'delete' ? 'Delete Forever' : 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Phone Dialog */}
      <EditProviderPhoneDialog
        open={editPhoneDialogOpen}
        onClose={() => setEditPhoneDialogOpen(false)}
        providerId={provider.provider_id}
        providerName={provider.name}
        currentPhone={provider.user_info?.mobile_phone_num}
      />

      {/* Delete Phone Confirmation Dialog */}
      <Dialog
        open={deletePhoneDialogOpen}
        onClose={() => setDeletePhoneDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Remove Phone Number</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Are you sure you want to remove the phone number for <strong>{provider.name}</strong>?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Current number: <strong>{provider.user_info?.mobile_phone_num}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              They will no longer receive SMS notifications at this number.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletePhoneDialogOpen(false)}
            disabled={isDeletingPhone}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeletingPhone}
            onClick={async () => {
              try {
                await deletePhone({
                  providerId: provider.provider_id,
                  updateProviderProfileRequest: {
                    mobile_phone_num: null,
                  },
                }).unwrap();
                enqueueSnackbar('Phone number removed successfully', { variant: 'success' });
                setDeletePhoneDialogOpen(false);
              } catch (error: any) {
                enqueueSnackbar(
                  error?.data?.message || error?.data?.detail || 'Failed to remove phone number',
                  { variant: 'error' }
                );
              }
            }}
          >
            {isDeletingPhone ? <CircularProgress size={20} /> : 'Remove Phone'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
